/**
 * WebSocket client for real-time hints (`/api/ws`).
 *
 * The socket never carries data a client must trust: an event says "something changed here" and
 * the application refetches from the REST API. Therefore correctness never depends on delivery:
 * after every (re)connect the owner of this client refetches what is on screen.
 *
 * - bounded exponential reconnect with jitter (1 s ... 30 s). A rejected session (4401) is
 *   reported (the viewer query re-checks it and takes the user to sign in) and retried at the
 *   same pace: the page may still have a valid session (a fresh cookie, a proxy hiccup);
 * - a socket that stays silent (no pong, no event) is declared dead and replaced - a half-open
 *   TCP connection never fires `close` on its own;
 * - reference-counted board AND database subscriptions, restored after a reconnect (a database
 *   subscription is accepted by the server only for holders of `database.view`);
 * - duplicate events (same id) and repeated ones (same board / database revision) are dropped;
 * - a heartbeat keeps presence alive and reports the card being edited.
 */

/** `open` = the server has answered on this connection (not merely: the handshake finished). */
export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export interface ServerEvent {
  type: string;
  id?: string;
  board_id?: string;
  database_id?: string;
  table_id?: string;
  card_id?: string;
  scope?: string;
  actor_id?: string;
  rev?: number;
  reason?: string;
  users?: string[];
  editing?: [string, string][];
  presence?: { users: string[]; editing: [string, string][] };
  [key: string]: unknown;
}

export interface SocketLike {
  readyState: number;
  send: (data: string) => void;
  close: (code?: number, reason?: string) => void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export interface RealtimeOptions {
  url: () => string;
  createSocket?: (url: string) => SocketLike;
  /** Test hooks. */
  random?: () => number;
  heartbeatMs?: number;
  now?: () => number;
}

const OPEN = 1;
const MAX_SEEN = 300;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
export const UNAUTHENTICATED_CODE = 4401;
/** Heartbeats the server may miss before the socket is considered dead. */
const MISSED_HEARTBEATS = 3;
/** A connection attempt that has not been answered by then is abandoned and retried. */
const CONNECT_TIMEOUT_MS = 15_000;

type Listener = (event: ServerEvent) => void;

export class RealtimeClient {
  private socket: SocketLike | null = null;
  private stateValue: ConnectionState = 'idle';
  private stopped = true;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<Listener>();
  private readonly stateListeners = new Set<(state: ConnectionState) => void>();
  private readonly boards = new Map<string, number>(); // board id -> subscribers
  private readonly databases = new Map<string, number>(); // database id -> subscribers
  private readonly editing = new Map<string, string | null>(); // board id -> card being edited
  private readonly seen = new Set<string>();
  private readonly revisions = new Map<string, number>();
  private everOpened = false;
  private lastSeenAt = 0;

  private readonly options: RealtimeOptions;

  constructor(options: RealtimeOptions) {
    this.options = options;
  }

  get state(): ConnectionState {
    return this.stateValue;
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onState(listener: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.clearConnectTimer();
    this.stopHeartbeat();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      socket.close(1000);
    }
    this.setState('idle');
  }

  /** Receive hints for a board while the returned function has not been called. */
  subscribeBoard(boardId: string): () => void {
    const count = this.boards.get(boardId) ?? 0;
    this.boards.set(boardId, count + 1);
    if (count === 0) this.send({ type: 'subscribe', board_id: boardId });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.boards.get(boardId) ?? 1) - 1;
      if (left <= 0) {
        this.boards.delete(boardId);
        this.editing.delete(boardId);
        this.revisions.delete(boardId);
        this.send({ type: 'unsubscribe', board_id: boardId });
      } else {
        this.boards.set(boardId, left);
      }
    };
  }

  /** Receive hints for a database (needs `database.view`) until the returned function is called. */
  subscribeDatabase(databaseId: string): () => void {
    const count = this.databases.get(databaseId) ?? 0;
    this.databases.set(databaseId, count + 1);
    if (count === 0) this.send({ type: 'subscribe_database', database_id: databaseId });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.databases.get(databaseId) ?? 1) - 1;
      if (left <= 0) {
        this.databases.delete(databaseId);
        this.revisions.delete(databaseId);
        this.send({ type: 'unsubscribe_database', database_id: databaseId });
      } else {
        this.databases.set(databaseId, left);
      }
    };
  }

  /** Report the card being edited on a board (``null`` = none). Sent at once and by heartbeat. */
  setEditing(boardId: string, cardId: string | null): void {
    if (this.editing.get(boardId) === cardId) return;
    this.editing.set(boardId, cardId);
    this.send({ type: 'presence', board_id: boardId, ...(cardId && { card_id: cardId }) });
  }

  private setState(state: ConnectionState): void {
    if (this.stateValue === state) return;
    this.stateValue = state;
    for (const listener of this.stateListeners) listener(state);
  }

  private send(message: Record<string, unknown>): void {
    if (this.socket?.readyState === OPEN) this.socket.send(JSON.stringify(message));
  }

  private connect(): void {
    if (this.stopped) return;
    this.setState('connecting');
    let socket: SocketLike;
    try {
      const url = this.options.url();
      socket = this.options.createSocket
        ? this.options.createSocket(url)
        : (new WebSocket(url) as unknown as SocketLike);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    // A handshake that hangs (a proxy that accepts the connection and never answers) fires
    // neither `open` nor `close`: without this the client would wait for ever.
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (this.socket === socket && this.stateValue !== 'open') this.dropSilentSocket();
    }, CONNECT_TIMEOUT_MS);
    socket.onopen = () => {
      // The handshake succeeded, but the connection is only healthy once the server has spoken:
      // it greets an accepted session at once ("hello"), and refuses one (4401 / 4403) with a
      // close frame right after the handshake. Reporting "open" here would flip a refused
      // connection open/closed in a loop and hide the very notice that explains it.
      this.lastSeenAt = this.now();
      for (const boardId of this.boards.keys()) {
        this.send({ type: 'subscribe', board_id: boardId });
      }
      for (const databaseId of this.databases.keys()) {
        this.send({ type: 'subscribe_database', database_id: databaseId });
      }
      this.startHeartbeat();
    };
    socket.onmessage = (message) => {
      this.lastSeenAt = this.now();
      this.confirmOpen();
      if (typeof message.data !== 'string') return;
      let event: ServerEvent;
      try {
        event = JSON.parse(message.data) as ServerEvent;
      } catch {
        return;
      }
      if (typeof event.type !== 'string') return;
      if (event.id !== undefined) {
        if (this.seen.has(event.id)) return;
        this.seen.add(event.id);
        if (this.seen.size > MAX_SEEN) {
          const oldest = this.seen.values().next().value;
          if (oldest !== undefined) this.seen.delete(oldest);
        }
      }
      if (event.type === 'board.changed' && event.board_id && typeof event.rev === 'number') {
        // Only an exact repeat is dropped. A LOWER revision is NOT "stale": the counter lives in
        // Redis and restarts from 1 when Redis is flushed or restarted - discarding those
        // would silence the board until the page is reloaded. A hint only triggers a refetch,
        // which always returns the latest state, so accepting an old one costs nothing.
        const last = this.revisions.get(event.board_id);
        if (last === event.rev) return;
        this.revisions.set(event.board_id, event.rev);
      }
      if (event.type === 'database.changed' && event.database_id && typeof event.rev === 'number') {
        // Same rule as for boards: only an exact repeat is dropped (the counter may restart).
        const last = this.revisions.get(event.database_id);
        if (last === event.rev) return;
        this.revisions.set(event.database_id, event.rev);
      }
      if (event.type === 'unsubscribed' && event.board_id && event.reason !== 'too_many') {
        // The server dropped it (access revoked / not found): do not ask again on reconnect.
        this.boards.delete(event.board_id);
        this.editing.delete(event.board_id);
      }
      if (event.type === 'unsubscribed' && event.database_id && event.reason !== 'too_many') {
        this.databases.delete(event.database_id);
        this.revisions.delete(event.database_id);
      }
      this.emit(event);
    };
    socket.onclose = (event) => {
      this.clearConnectTimer();
      this.stopHeartbeat();
      this.socket = null;
      this.setState('closed');
      if (this.stopped) return;
      if (event.code === UNAUTHENTICATED_CODE) this.emit({ type: 'unauthenticated' });
      this.scheduleReconnect();
    };
    socket.onerror = () => {
      // Followed by `close`; nothing to do here.
    };
  }

  /** The server answered: the connection is healthy. */
  private confirmOpen(): void {
    if (this.stateValue === 'open') return;
    this.clearConnectTimer();
    // Anything that failed before (also the very first attempt) means events may be missed.
    const reconnected = this.everOpened || this.attempt > 0;
    this.attempt = 0;
    this.everOpened = true;
    this.setState('open');
    // Whatever happened while the socket was down is unknown: the owner refetches.
    if (reconnected) this.emit({ type: 'reconnected' });
  }

  private emit(event: ServerEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.timer !== null) return;
    const random = this.options.random ?? Math.random;
    const exponential = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** this.attempt);
    this.attempt += 1;
    const delay = Math.round(exponential / 2 + (random() * exponential) / 2);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }

  private clearConnectTimer(): void {
    if (this.connectTimer !== null) clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  private now(): number {
    return (this.options.now ?? Date.now)();
  }

  /** The socket looks open but nothing arrives: drop it and reconnect. */
  private dropSilentSocket(): void {
    const socket = this.socket;
    this.stopHeartbeat();
    this.clearConnectTimer();
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close(4000, 'silent');
      } catch {
        // already gone
      }
    }
    this.setState('closed');
    this.scheduleReconnect();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const interval = this.options.heartbeatMs ?? 15_000;
    this.heartbeat = setInterval(() => {
      if (this.now() - this.lastSeenAt >= interval * MISSED_HEARTBEATS) {
        this.dropSilentSocket();
        return;
      }
      if (this.boards.size === 0) {
        this.send({ type: 'ping' });
        return;
      }
      for (const boardId of this.boards.keys()) {
        const card = this.editing.get(boardId);
        this.send({ type: 'presence', board_id: boardId, ...(card && { card_id: card }) });
      }
    }, interval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }
}

/** Same-origin WebSocket URL of the API. */
export function realtimeUrl(apiBase: string): string {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}${apiBase}/ws`;
}
